import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Event } from '../types';

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvent = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/events/${eventId}`);
      setEvent(response.data.data);
    } catch (err: any) {
      console.error('Failed to load event:', err);
      setError(err.response?.data?.error?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isCreator = event && user && event.creator_id === user.sub;
  const isCoOrganizer = event && user && event.co_organizers?.includes(user.sub);
  const canManage = isCreator || isCoOrganizer;

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error || 'Event not found'}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/events')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Events
        </button>
      </div>
    );
  }

  const isSingleDay = event.start_date === event.end_date;

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <button
            className="btn btn-link text-decoration-none mb-3"
            onClick={() => navigate('/events')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Events
          </button>

          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="mb-2">{event.name}</h1>
              <div className="mb-2">
                <span className={`badge ${event.status === 'public' ? 'bg-success' : 'bg-secondary'} me-2`}>
                  {event.status === 'public' ? 'Public' : 'Draft'}
                </span>
                {isCreator && (
                  <span className="badge bg-primary">Creator</span>
                )}
                {isCoOrganizer && !isCreator && (
                  <span className="badge bg-info">Co-Organizer</span>
                )}
              </div>
            </div>
            {canManage && (
              <div>
                <Link
                  to={`/events/${eventId}/edit`}
                  className="btn btn-primary"
                >
                  <i className="fas fa-edit me-2"></i>
                  Edit Event
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Details */}
      <div className="row">
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fas fa-info-circle me-2"></i>
                Event Details
              </h5>

              <div className="mb-3">
                <h6 className="text-muted mb-1">
                  <i className="fas fa-calendar me-2"></i>
                  Date
                </h6>
                <p className="mb-0">
                  {isSingleDay
                    ? formatDate(event.start_date)
                    : `${formatDate(event.start_date)} - ${formatDate(event.end_date)}`}
                </p>
              </div>

              <div className="mb-3">
                <h6 className="text-muted mb-1">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  Location
                </h6>
                <p className="mb-0">{event.location}</p>
              </div>

              <div className="mb-3">
                <h6 className="text-muted mb-1">
                  <i className="fas fa-align-left me-2"></i>
                  Description
                </h6>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {event.description}
                </p>
              </div>
            </div>
          </div>

          {/* Sessions Section - Placeholder */}
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fas fa-microphone me-2"></i>
                Sessions
              </h5>
              <div className="text-center text-muted py-4">
                <i className="fas fa-calendar-times fa-3x mb-3"></i>
                <p>No sessions scheduled yet.</p>
                {canManage && (
                  <button className="btn btn-outline-primary" disabled>
                    <i className="fas fa-plus me-2"></i>
                    Add Session (Coming Soon)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          {/* Organizers */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fas fa-users me-2"></i>
                Organizers
              </h5>

              <div className="mb-3">
                <h6 className="text-muted small mb-2">Creator</h6>
                <div className="d-flex align-items-center">
                  <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                  <div>
                    <div className="fw-bold">{event.creator_name || 'Unknown'}</div>
                  </div>
                </div>
              </div>

              {event.co_organizer_details && event.co_organizer_details.length > 0 && (
                <div>
                  <h6 className="text-muted small mb-2">Co-Organizers</h6>
                  {event.co_organizer_details.map((coOrg) => (
                    <div key={coOrg.user_id} className="d-flex align-items-center mb-2">
                      {coOrg.profile_image_url ? (
                        <img
                          src={coOrg.profile_image_url}
                          alt={coOrg.name}
                          className="rounded-circle me-2"
                          style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                        />
                      ) : (
                        <i className="fas fa-user-circle fa-2x text-secondary me-2"></i>
                      )}
                      <div>
                        <div className="fw-bold">{coOrg.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {canManage && (
                <div className="mt-3">
                  <Link
                    to={`/events/${eventId}/organizers`}
                    className="btn btn-sm btn-outline-primary w-100"
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    Manage Co-Organizers
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          {canManage && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="fas fa-bolt me-2"></i>
                  Quick Actions
                </h5>
                <div className="d-grid gap-2">
                  <Link
                    to={`/events/${eventId}/edit`}
                    className="btn btn-outline-primary"
                  >
                    <i className="fas fa-edit me-2"></i>
                    Edit Event
                  </Link>
                  <button className="btn btn-outline-secondary" disabled>
                    <i className="fas fa-user-plus me-2"></i>
                    Invite Speaker (Coming Soon)
                  </button>
                  <button className="btn btn-outline-secondary" disabled>
                    <i className="fas fa-envelope me-2"></i>
                    Send Message (Coming Soon)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Admin Actions */}
          {userRole === 'admin' && (
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="fas fa-shield-alt me-2"></i>
                  Admin Actions
                </h5>
                <div className="d-grid gap-2">
                  <Link
                    to={`/events/${eventId}/transfer`}
                    className="btn btn-outline-danger"
                  >
                    <i className="fas fa-exchange-alt me-2"></i>
                    Transfer Ownership
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
